'use client';

/**
 * Fills its `position: relative` parent with a course cover: the real
 * image when one loads, otherwise the tenant's brand mark (light/dark
 * aware, via {@code useBrand}) centered over a gradient.
 *
 * <p>Treats a 404'd image URL (deleted asset, bad migration, ...) the
 * same as a genuinely absent one — {@code next/image}'s {@code onError}
 * flips the same fallback instead of leaving the browser's broken-image
 * glyph on screen, which is what every card looked like before this
 * existed (each one hand-rolled its own `imageUrl ? <NextImage> :
 * gradient` with no error handling).
 *
 * <p>Renders only the image/fallback layer — callers still own their own
 * `position: relative` + `aspectRatio` wrapper and any overlay siblings
 * (level badge, play button, etc.) stacked after it in DOM order.
 */
import { useState } from 'react';
import NextImage from 'next/image';
import { useBrand, type BrandInfo } from '@akademi/site-builder-runtime';
import { resizeImageUrl } from './resizeImageUrl';

const DEFAULT_GRADIENT =
  'linear-gradient(135deg, var(--color-brand-100, #26c6da) 0%, var(--color-brand-200, #006d82) 100%)';

export function CourseCoverImage({
  image,
  resizeTo = '680x0',
  sizes = '(max-width: 640px) 100vw, 340px',
  gradient = DEFAULT_GRADIENT,
  hideBrandMark = false,
  padding = '22%',
}: {
  image?: string;
  /** Passed straight to {@link resizeImageUrl}'s `size` arg — match the
   *  card's real render width so the CDN doesn't ship a full-res asset. */
  resizeTo?: string;
  /** `next/image`'s `sizes` attribute — match the card's layout. */
  sizes?: string;
  /** No-cover background. Defaults to the shared brand-teal gradient;
   *  pass a card-specific one (e.g. Marketplace's indigo/purple) to
   *  preserve that card's existing look. */
  gradient?: string;
  /** Suppress the brand mark while still showing the gradient — e.g. a
   *  hover-preview surface that's about to show a video/play badge in
   *  the same spot and doesn't want the logo competing with it. */
  hideBrandMark?: boolean;
  /** Padding around the brand mark when it's centered in the cover. */
  padding?: string;
}) {
  const imageUrl = typeof image === 'string' ? resizeImageUrl(image, resizeTo) : undefined;
  const [failed, setFailed] = useState(false);
  const brand = useBrand();
  const showImage = Boolean(imageUrl) && !failed;

  if (showImage) {
    return (
      <NextImage
        src={imageUrl!}
        alt=""
        fill
        sizes={sizes}
        style={{ objectFit: 'cover' }}
        onError={() => setFailed(true)}
      />
    );
  }

  const hasBrandLogo = !hideBrandMark && !!(brand?.logo || brand?.logoDark);
  return (
    <div style={{ position: 'absolute', inset: 0, background: gradient }}>
      {hasBrandLogo && <CourseCoverBrandMark brand={brand} padding={padding} />}
    </div>
  );
}

function CourseCoverBrandMark({ brand, padding }: { brand: BrandInfo | null; padding: string }) {
  const alt = brand?.name || '';
  const imgStyle = { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' as const };
  const boxStyle = {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding,
  };
  return (
    <div style={boxStyle}>
      {brand?.logo && brand?.logoDark ? (
        <>
          <img className="lms-logo--light" src={brand.logo} alt={alt} style={imgStyle} />
          <img className="lms-logo--dark" src={brand.logoDark} alt={alt} style={imgStyle} />
        </>
      ) : (
        <img src={brand?.logo || brand?.logoDark} alt={alt} style={imgStyle} />
      )}
    </div>
  );
}
